import React, { useEffect, useRef, useCallback, forwardRef } from 'react';
import Icon from 'components/Icon';
import classnames from 'classnames';
import { useStore, StoreTypes } from 'context';
import * as types from 'constants/actionTypes';
import styles from './index.module.scss';

const AudioButton = forwardRef(({
  className,
  src,
  children,
  autoPlay = false,
  empty = false,
  disabled = false,
  onPlaying = () => { },
  onEnded = () => { },
  onClick,
}, ref) => {
  const audioRef = useRef();
  const [{ audioManager }, globalDispatch] = useStore(StoreTypes.global);

  const playingHandler = useCallback(e => {
    onPlaying();
  }, [onPlaying])

  const endedHandler = useCallback(e => {
    onEnded(e);
  }, [onEnded]);

  const playAudio = useCallback(() => {
    if (!audioRef.current) return;

    // 停止當前播放的音檔
    if (audioManager.currentPlayingAudioElement && 
        audioManager.currentPlayingAudioElement !== audioRef.current) {
        audioManager.currentPlayingAudioElement.currentTime = 0;
        audioManager.currentPlayingAudioElement.pause(); 
        audioManager.currentPlayingAudioElement.dispatchEvent(new CustomEvent('ended', {
          detail: { forced: true }
        })); 
    }

    // 設定新的音檔並播放
    globalDispatch({
      type: types.SET_CURRENT_PLAYING_AUDIO,
      payload: {
        audioElement: audioRef.current
      }
    });
    
    audioRef.current.play();
  }, [audioManager.currentPlayingAudioElement, globalDispatch]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.addEventListener('play', playingHandler);
    audioRef.current.addEventListener('ended', endedHandler);
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('play', playingHandler);
        audioRef.current.removeEventListener('ended', endedHandler);
        audioRef.current.pause();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    if (autoPlay) {
      playAudio();
    }
  }, [autoPlay, playAudio]);

  const clickHandler = e => {
    if (disabled) return;
    if (onClick) {
      onClick(audioRef.current);
      return;
    }
    
    if (audioRef.current.paused) {
      playAudio();
    } else {
      audioRef.current.pause();
      globalDispatch({ type: types.CLEAR_CURRENT_PLAYING_AUDIO });
    }
  }

  return (
    <>
      <audio 
        ref={audioRef} 
        src={src}
        playsInline
        crossOrigin="anonymous"
      />
      <button
        ref={ref}
        className={classnames(styles.audioButton, className)}
        onClick={clickHandler}
      >
        {children || empty ? children : <Icon type="text" name="headphones" />}
      </button>
    </>
  );
});

export default AudioButton;
